/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/

import {AnalysisService} from '@/services/AnalysisService'
import AnalysisServiceMixin from '@/services/AnalysisServiceMixin'
import {AnalysisVO} from '@/valueObjects/AnalysisVO'
import ConfirmationModal from '@/components/shared/ConfirmationModal'
import {Getter} from 'vuex-class'
import {UserVO} from '@/valueObjects/UserVO'
import MessageBox from '@/components/shared/MessageBox'
import {displayLocalDate} from '@/utils/DateUtils'
import {API_BASE_PATH, ERROR_MESSAGE_TYPE, SUCCESS_MESSAGE_TYPE} from '@/utils/constants'

import {Component, Prop, Vue} from 'vue-property-decorator'
import ReportValidator from '@/utils/validators/ReportValidator'
import {INVALID_VALUE_GUI_MARK} from '@/utils/constants'

const ITEM_PER_REQUEST: number = 10

@Component({
    components: {
        ConfirmationModal,
        MessageBox,
    },
    mixins: [AnalysisServiceMixin],
})

export default class AnalysisList extends Vue {

    @Getter('getCurrentUser') private currentUser: UserVO

    private modalContent = {
        confirmButtonLabel: 'Delete',
        message: '',
        title: 'Confirm analysis removal',
    }
    private isConfirmationVisible: boolean = false
    private isCopy: boolean = false
    private message = 'test'
    private analysisService: AnalysisService
    private searchString = ''
    private analyses: AnalysisVO[] = []
    private selectedAnalysis: AnalysisVO = null
    private loadedPageCount = 1
    private hasMore: boolean = true
    private messageType = ''
    private apiBasePath = API_BASE_PATH

    public async loadAnalyses(): Promise<void> {
        this.analyses = []
        this.loadedPageCount = 1
        this.hasMore = false
        this.message = ''
        try {
            await this.loadAnalysesPage()
            this.displayInfoMessage()
        } catch (e) {
            this.message = 'Loading of analyses results failed. ' + e.errorMessage
            this.messageType = ERROR_MESSAGE_TYPE
        }
    }

    public async loadMore() {
        this.message = ''
        this.loadedPageCount++
        try {
            await this.loadAnalysesPage()
        } catch (e) {
            this.message = e.errorMessage
            this.messageType = ERROR_MESSAGE_TYPE
        }
    }

    public postAnalysis() {
        this.$router.push({name: 'newanalysis'})
    }

    public async fileHash(file: any) {

        const arrayBuffer = await file.arrayBuffer();
      
        // Use the subtle crypto API to perform a SHA256 Sum of the file's Array Buffer
        // The resulting hash is stored in an array buffer
        const hashAsArrayBuffer = await crypto.subtle.digest('SHA-1', arrayBuffer);
      
        // To display it as as a string we will get the hexadecimal value of each byte of the array buffer
        // This gets us an array where each byte of the array buffer becomes one item in the array
        const uint8ViewOfHash = new Uint8Array(hashAsArrayBuffer);
        // We then convert it to a regular array so we can convert each item to hexadecimal strings
        // Where to characters of 0-9 or a-f represent a number between 0 and 16, containing 4 bits of information, so 2 of them is 8 bits (1 byte).
        const hashAsString = Array.from(uint8ViewOfHash).map(b => b.toString(16).padStart(2, '0')).join('');
        return hashAsString;
    }
    
    public async uploadIcon(FileList: FileList){
        let hash = await this.fileHash(FileList[0])
        this.searchString = hash
    }
    
    public mounted() {
        this.loadAnalyses()
        if (this.$parent) { // to make the unit tests work
            this.$parent.$on('resetAnalyses', this.reset)
        }
        document.addEventListener('scroll', this.handleScroll)
    }

    public handleScroll(){
        if(scrollY+innerHeight>=document.body.scrollHeight){
            this.loadMore()
        }
    }
    public reset() {
        this.searchString = ''
        this.loadAnalyses()
    }

   private async loadAnalysesPage() {

    const newAnalyses = await this.analysisService.getAnalyses(this.loadedPageCount,
        ITEM_PER_REQUEST,
        this.searchString)
    if (newAnalyses.length > 0) {
        this.analyses = this.analyses.concat(newAnalyses)
    }
    this.hasMore = newAnalyses.length === ITEM_PER_REQUEST

    }

    private displayInfoMessage() {
        if (this.analyses.length === 0) {
            this.messageType = SUCCESS_MESSAGE_TYPE
            if (this.searchString === '') {
                this.message = 'You have not yet requested any analysis. Click "Submit an app" to start one.'
            } else {
                this.message = 'No analysis matches this search condition.'
            }
        } else {
            this.message = ''
        }
    }

    private clearMessage() {
        this.message = ''
    }

    private hasAnalyses() {
        return this.analyses && this.analyses.length > 0
    }

    private copyToClipBoard(hash: string) {
        this.isCopy=true
        //console.log("copyToClipBoard " + this.isCopy)
        function handler(event: any) {
            event.clipboardData.setData('text/plain', hash)
            event.preventDefault()
            document.removeEventListener('copy', handler, true)
        }

        document.addEventListener('copy', handler, true)
        document.execCommand('copy')
    }

    private formatDate(date: string): string {
        return displayLocalDate(date)
    }

    private formatName(name: string): string {
        if (name.length > 15 ) {
            return name.substr(0, 15) + "..."
        }
        else {
            return name
        }
    }

    private detectEnter(event: KeyboardEvent) {
        if (event.code === 'Enter') {
            this.loadAnalyses()
        }
    }

    private getStatus(currentAnalysis: AnalysisVO): string {
        if (AnalysisVO.getStatus(currentAnalysis) === "Analysis in progress") {
            return "fas fa-spinner fa-pulse"
        }
        if (AnalysisVO.getStatus(currentAnalysis) === "Analysis finished - ok") {
            return "fas fa-check-square"
        }
        if (AnalysisVO.getStatus(currentAnalysis) === "Analysis finished - error") {
            return "fas fa-ban"
        }
        return AnalysisVO.getStatus(currentAnalysis)
    }

    private isDeletable(analysis: AnalysisVO): boolean {
        return ((this.currentUser.isAdmin()) ||
            (this.currentUser.id === analysis.userId))
    }

    private confirmAnalysisDeletion(analysis: AnalysisVO) {
        this.selectedAnalysis = analysis
        this.modalContent.message = `You will delete the analysis of "${analysis.fileName}". This cannot be undone.`
        this.isConfirmationVisible = true
    }

    private cancelModal() {
        this.isConfirmationVisible = false
    }

    private confirmModal() {
        this.isConfirmationVisible = false
        this.deleteSelectedAnalysis()
    }

    private async deleteSelectedAnalysis(): Promise<void> {
        try {
            await this.analysisService.deleteAnalysis(this.selectedAnalysis)
            this.selectedAnalysis = null
            this.loadAnalyses()
        } catch (e) {
            this.message = `An error prevented to delete the analysis: ${e.errorMessage}`
            this.messageType = ERROR_MESSAGE_TYPE
        }
    }

    private async loadAppIcon(analysisId: number,currentAnalysis: AnalysisVO){
        const app_icon = await this.analysisService.getAPKIcon(analysisId, currentAnalysis.staticReport.icon_path)
        return app_icon
    }


    private getUrlIcon(currentAnalysis: AnalysisVO): string{
        if (currentAnalysis !== undefined) {
            if (! ReportValidator.hasStaticField(currentAnalysis, 'icon_path')) {
                currentAnalysis.staticReport.icon_path="test"                
            }
            return `${this.apiBasePath}/analysis/${currentAnalysis.id}/apk_icon?icon_path=${currentAnalysis.staticReport.icon_path}`
        }
        else {
            return ""
        }
    }

    private async hideIcon(currentAnalysis: AnalysisVO): Promise<void> {
        var analysis_id = currentAnalysis.id
        try {
            await this.analysisService.hideIcon(analysis_id)
            this.loadAnalyses()
        } catch (e) {
            this.message = `An error prevented to hidding the icon the analysis: ${e.errorMessage}`
            this.messageType = ERROR_MESSAGE_TYPE
        }

    }

    private clearInput() {
        this.searchString = ''
    }

    private getAppName(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis !== undefined) {
            if (! ReportValidator.hasStaticField(currentAnalysis, 'app_name')) {
                return ''
            }
            return currentAnalysis.staticReport.app_name
        }
    }

    private getSizeApp(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis !== undefined) {
            if (! ReportValidator.hasStaticField(currentAnalysis, 'app_size')) {
                return ''
            }
            return currentAnalysis.staticReport.app_size
        }
    }

    private getTypeApp(currentAnalysis: AnalysisVO): string {
        //console.log(currentAnalysis)
        if (currentAnalysis !== undefined) {
                return  currentAnalysis.app_type
            
        }
        return ""
    }

    private getManifest(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis !== undefined) {
            if (! ReportValidator.hasStaticField(currentAnalysis, 'manifest_number')) {
                return '.'
            }
            return currentAnalysis.staticReport.manifest_number.toString()
        }
    }

    private getPermissions(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis !== undefined) {
            if (! ReportValidator.hasStaticField(currentAnalysis, 'permissions_number')) {
                return '.'
            }
            return currentAnalysis.staticReport.permissions_number.toString()
        }
    }

    private getSignature(currentAnalysis: AnalysisVO): string {
        let nb = 0
        if (currentAnalysis.staticReport !== null) {
            if (currentAnalysis.staticReport.stat_yara_matches_number !== undefined) {
                nb = currentAnalysis.staticReport.stat_yara_matches_number
            }
            
        }
        if (currentAnalysis.dynamicReport !== null) {
            if (currentAnalysis.dynamicReport.dyn_yara_matches_number !== undefined) {
                nb+=currentAnalysis.dynamicReport.dyn_yara_matches_number
            }
        }
        return nb.toString()
    }

    private get_info(currentAnalysis: AnalysisVO): string {
        return "test"
    }

    private get_nb_Signature_dump(currentAnalysis: AnalysisVO): string {
        let nb = 0
        if (currentAnalysis.dynamicReport !== null) {
            if (currentAnalysis.dynamicReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.dynamicReport.yara_matches.length ; cpt++){
                    //console.log(currentAnalysis.dynamicReport.yara_matches[cpt].category)
                    if (currentAnalysis.dynamicReport.yara_matches[cpt].category == "memory_dump")
                    nb+=1
                }
            }
        }
        return nb.toString()
    }
    private get_Signature_dump(currentAnalysis: AnalysisVO): string {
        let nb = ""
        if (currentAnalysis.dynamicReport !== null) {
            if (currentAnalysis.dynamicReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.dynamicReport.yara_matches.length ; cpt++){
                    if (currentAnalysis.dynamicReport.yara_matches[cpt].category == "memory_dump"){
                        //console.log(currentAnalysis.dynamicReport.yara_matches[cpt].rule_set_name)
                        nb=nb+currentAnalysis.dynamicReport.yara_matches[cpt].rule_set_name + " "
                    }
                }
            }
        }
        return nb.toString()
    }
    private get_nb_Signature_hook(currentAnalysis: AnalysisVO): string {
        let nb = 0
        if (currentAnalysis.dynamicReport !== null) {
            if (currentAnalysis.dynamicReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.dynamicReport.yara_matches.length ; cpt++){
                    //console.log(currentAnalysis.dynamicReport.yara_matches[cpt].category)
                    if (currentAnalysis.dynamicReport.yara_matches[cpt].category == "network")
                    nb+=1
                }
            }
        }
        return nb.toString()
    }
    private get_Signature_hook(currentAnalysis: AnalysisVO): string {
        let nb = ""
        if (currentAnalysis.dynamicReport !== null) {
            if (currentAnalysis.dynamicReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.dynamicReport.yara_matches.length ; cpt++){
                    if (currentAnalysis.dynamicReport.yara_matches[cpt].category == "network"){
                        //console.log(currentAnalysis.dynamicReport.yara_matches[cpt].rule_set_name)
                        nb=nb+currentAnalysis.dynamicReport.yara_matches[cpt].rule_set_name + " "
                    }
                }
            }
        }
        return nb.toString()
    }
    private get_nb_Signature_code(currentAnalysis: AnalysisVO): string {
        let nb = 0
        if (currentAnalysis.staticReport !== null) {
            if (currentAnalysis.staticReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.staticReport.yara_matches.length ; cpt++){
                    //console.log(currentAnalysis.staticReport.yara_matches[cpt].category)
                    if (currentAnalysis.staticReport.yara_matches[cpt].category == "source_code")
                    nb+=1
                }
            }
        }
        return nb.toString()
    }
    private get_Signature_code(currentAnalysis: AnalysisVO): string {
        let nb = ""
        if (currentAnalysis.staticReport !== null) {
            if (currentAnalysis.staticReport.yara_matches !== undefined) {
                for (let cpt = 0 ; cpt <currentAnalysis.staticReport.yara_matches.length ; cpt++){
                    if (currentAnalysis.staticReport.yara_matches[cpt].category == "source_code"){
                        //console.log(currentAnalysis.staticReport.yara_matches[cpt].rule_set_name)
                        nb=nb+currentAnalysis.staticReport.yara_matches[cpt].rule_set_name + " "
                    }
                }
            }
        }
        return nb.toString()
    }

    private getClassNameManifest(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis.staticReport != null) {
            if (currentAnalysis.staticReport.manifest_severity !== undefined) {
                return currentAnalysis.staticReport.manifest_severity
            }
        }
    }

    private getClassNamePermissions(currentAnalysis: AnalysisVO): string {
        if (currentAnalysis.staticReport != null) {
            if (currentAnalysis.staticReport.permissions_severity !== undefined) {
                return currentAnalysis.staticReport.permissions_severity 
            } 
        }
    }

    private getClassNameSignature(currentAnalysis: AnalysisVO): string {
        let severity = -1
        if (currentAnalysis.staticReport != null) {
            if (currentAnalysis.staticReport.stat_yara_matches_severity !== undefined)  {
                if (severity < currentAnalysis.staticReport.stat_yara_matches_severity) {
                    severity = currentAnalysis.staticReport.stat_yara_matches_severity
                }
            }
        } 
        if (currentAnalysis.dynamicReport != null) {
            if (currentAnalysis.dynamicReport.dyn_yara_matches_severity !== undefined)  {
                if (severity < currentAnalysis.dynamicReport.dyn_yara_matches_severity) {
                    severity = currentAnalysis.dynamicReport.dyn_yara_matches_severity
                }
            }
        }
        if (severity==4){
            return 'has-background-danger'
        }
        else if (severity==3){
            return 'has-background-warning'
        }
        else if (severity==2){
            return 'has-background-grey-light'
        }
        else if (severity==0){
            return 'has-background-success'
        }
    }

    private getClassNameFamily(currentAnalysis: AnalysisVO): string {
            return 'has-background-danger'
    }

    private async loadRapport(currentAnalysis: AnalysisVO): Promise<AnalysisVO> {
        let analysis: AnalysisVO
        let isFinished: boolean
        [analysis, isFinished] = await this.analysisService.getAnalysis(currentAnalysis.id)
        return analysis

    }

    private loadSummary(analysis: AnalysisVO) {
       if ( !this.isConfirmationVisible && !this.isCopy ) {
       let routedata = this.$router.resolve({name: 'analysis-summary', params: { analysisId: analysis.id.toString()}})
       window.open(routedata.href, '_blank')
       }
       else{
       this.isCopy=false
       }
    }
}
